/**
 * Circular dependency prevention:
 * - useBasket
 */
import { computed, reactive, readonly } from 'vue'
import { add } from 'date-fns'

import { events$, loading$ } from '@/services'

import { SESSION_EOL, STORAGE_KEY } from '@/config/constants'
import { EVENT_SESSION } from '@/config/events'

import { DateTimeHelper } from '@/helpers/DateTime'

import useBasket from '@/hooks/useBasket'
import useLogger from '@/hooks/useLogger'
import useTranslation from '@/hooks/useTranslation'

const session = reactive({
  active: false,
  basketId: null,
})
const consoleColor = 'color: green'

const { logInfo, logError } = useLogger()

// eslint-disable-next-line sonarjs/cognitive-complexity
async function autostart(payload = {}, forcedOverwrite = false) {
  const { createDefaultBasket, createOvpBasket, persistBasket } = useBasket()

  // session is active, just return data
  // can only be executed, if autostart is triggered manually
  if (session.active && forcedOverwrite === false) {
    return $_getFromStorage()
  } else {
    const defaultBasket = await createDefaultBasket(payload)

    const dataFromStorage = $_getFromStorage()
    let basket

    if (!dataFromStorage.hot && dataFromStorage.updateDate && window.ineum) {
      window.ineum('reportError', 'basket_id_already_available')
    }

    // resume session from localStorage.
    // @NOTE ((dataFromStorage.hot || dataFromStorage.updateDate) is a workaround, to fix issue with basket_id_already_available)
    if (dataFromStorage && (dataFromStorage.hot || dataFromStorage.updateDate) && !forcedOverwrite) {
      const { updateTranslations } = useTranslation()

      // try to resume. if it fails, restart
      try {
        basket = await resumeSession(dataFromStorage)
      } catch (error) {
        return autostart({}, true)
      }

      Object.assign(basket, payload)

      await updateTranslations({ locale: basket.language })
      await setSession(basket)
      await persistBasket(basket)
    } else {
      // create local basket-session
      if (!forcedOverwrite) {
        basket = Object.assign({}, defaultBasket, dataFromStorage)

        await setSession(basket, { hot: false })
        await persistBasket(basket)

        // create 'live' basket-session
      } else {
        basket = await createOvpBasket(defaultBasket)
      }
    }

    return basket
  }
}

function clearSession() {
  if (!session.active) return Promise.reject()

  const basketId = session.basketId
  session.active = false

  if (basketId) {
    logInfo(['%cCLEARED SESSION', consoleColor, basketId])
  }

  window.localStorage.removeItem(STORAGE_KEY)
  return Promise.resolve()
}

function $_getFromStorage() {
  const item = window.localStorage.getItem(STORAGE_KEY)
  let value
  try {
    value = item && JSON.parse(item)
  } catch (e) {
    logError(['Failed to get session from localstorage\n', e, item])
  }

  const hasExpired = (value && value.eol && !DateTimeHelper.isInFuture(new Date(value.eol))) || !value?.basketId

  if (!value || hasExpired) {
    return false
  } else {
    return { ...value }
  }
}

/**
 * try resuming a session, either from localstorage (hot:false), or from the backend(hot:true)
 * @note: at this step, we don't have the basket from the be yet, so you need to check the state from the localStorage
 */
async function resumeSession(payload) {
  const { fetchBasket } = useBasket()
  const { basketId } = payload

  let _basket
  if (!payload.hot) {
    _basket = payload
  } else {
    _basket = await fetchBasket({ basketId })
  }

  if (!_basket) {
    loading$.failed()
    throw new Error(`failed to load basket ${basketId}`)
  }

  logInfo(['%cRESUME SESSION', consoleColor, basketId])
  events$.emit(EVENT_SESSION.RESUME, basketId)
  return _basket
}

/**
 * save "session" into localStorage item
 *
 * @param <Object>payload - basketObject
 * @params <Object>options - hot; setOwner;
 *
 */
function setSession(payload, options = { hot: true }) {
  const __localSession = $_getFromStorage()
  const { basketId, jwToken, updateDate } = payload
  const _session = { basketId, jwToken, updateDate: updateDate?.substring(0, 24) }

  if (__localSession.hot) {
    _session.eol = add(new Date(), SESSION_EOL)
    _session.hot = true
  } else {
    if ((!payload.email || !payload.mobile) && options.hot) {
      _session.eol = add(new Date(), SESSION_EOL)
    }
    _session.hot = options.hot
  }

  // log basketId to instana
  if (window.ineum) {
    window.ineum('user', basketId)
  }

  session.basketId = _session.basketId
  session.active = true
  session.jwToken = jwToken

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(_session))

  logInfo(['%cSESSION UPDATED', consoleColor, basketId])
  events$.emit(EVENT_SESSION.UPDATE, basketId)
  return Promise.resolve(_session)
}

export default function useSession() {
  return {
    autostart,
    clearSession,
    isActive: computed(() => session.active),
    resumeSession,
    setSession,

    session: readonly(session),
  }
}
